import api from '@/api';
import { message } from 'antd';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import {
    LEFT_WIDGET,
    RIGHT_WIDGET,
    DEFAULT_PARAMETERS_FOR_SOLVING,
    INDICATORS,
    CURRENT_TAB,
    STEPS_INITIAL_STATE,
} from '@/components/DesaturationCharacteristics/constants';

const desaturationCharacteristicsModel = {
    name: 'desaturationCharacteristics',
    state: {
        currentLeftWidget: LEFT_WIDGET.spreadsheets,
        currentRightWidget: RIGHT_WIDGET.GUR,
        currentTab: CURRENT_TAB.calculationParameters,
        solved: false,
        approximationData: {
            tableData: null,
            graphData: null,
            loading: false,
        },
        firstIndicator: {
            value: INDICATORS.base.id,
            label: INDICATORS.base.name,
            xAxisType: INDICATORS.base.xAxisType,
            tableData: null,
            graphData: null,
            loading: false,
        },
        secondIndicator: {
            value: INDICATORS.waterCut.id,
            label: INDICATORS.waterCut.name,
            xAxisType: INDICATORS.waterCut.xAxisType,
            tableData: null,
            graphData: null,
            loading: false,
        },
        indicators: {
            base: null,
            debitWaterCut: null,
            orfWaterCut: null,
            owr: null,
            tempGur: null,
            upTake: null,
            upTakeWaterCut: null,
            waterCut: null,
            waterCutCumSum: null,
        },
        stepsData: null,
        methodsData: null,
        GURData: null,
        injectBalanceData: null,
        productionPumpingData: null,
        // solvedResultParameters: DEFAULT_PARAMETERS_SOLVED,
        solvedResultParameters: DEFAULT_PARAMETERS_FOR_SOLVING,
        unsolvedResultParameters: DEFAULT_PARAMETERS_FOR_SOLVING,
        loading: {
            methodsData: false,
            GURData: false,
            injectBalanceData: false,
            productionPumpingData: false,
            stratumsData: false,
            stepsData: false,
        },
    },

    reducers: {
        setCurrentLeftWidget(state, payload) {
            return { ...state, currentLeftWidget: payload };
        },
        setCurrentRightWidget(state, payload) {
            return { ...state, currentRightWidget: payload };
        },
        setStratumsData(state, payload) {
            return {
                ...state,
                unsolvedResultParameters: { ...state.unsolvedResultParameters, stratumsData: payload },
                solvedResultParameters: { ...state.solvedResultParameters, stratumsData: payload },
            };
        },
        setMethodsData(state, payload) {
            return { ...state, methodsData: payload };
        },
        setSolved(state, payload) {
            return { ...state, solved: payload };
        },
        setCurrentTab(state, payload) {
            return { ...state, currentTab: payload };
        },
        setValuesForCalculation(state, payload) {
            return { ...state, valuesForCalculation: { ...state.valuesForCalculation, ...payload } };
        },
        setSolvedResultParametersValue(state, payload) {
            return { ...state, solvedResultParameters: payload };
        },
        setUnsolvedResultParametersValue(state, payload) {
            return { ...state, unsolvedResultParameters: { ...state.unsolvedResultParameters, ...payload } };
        },
        setInjectBalanceData(state, payload) {
            return { ...state, injectBalanceData: payload };
        },
        setProductionPumpingData(state, payload) {
            return { ...state, productionPumpingData: payload };
        },
        setGURData(state, payload) {
            return { ...state, GURData: payload };
        },
        setFirstIndicatorData(state, payload) {
            return { ...state, firstIndicator: { ...state.firstIndicator, ...payload } };
        },
        setSecondIndicatorData(state, payload) {
            return { ...state, secondIndicator: { ...state.secondIndicator, ...payload } };
        },
        setApproximationData(state, payload) {
            return { ...state, approximationData: { ...state.approximationData, ...payload } };
        },
        setStepsData(state, payload) {
            return { ...state, stepsData: { ...state.stepsData, ...payload } };
        },
        setIndicators(state, payload) {
            return { ...state, indicators: { ...state.indicators, ...payload } };
        },
        /* loading */
        setMethodsDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    methodsData: payload,
                },
            };
        },
        setGURDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    GURData: payload,
                },
            };
        },
        setInjectBalanceDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    injectBalanceData: payload,
                },
            };
        },
        setProductionPumpingDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    productionPumpingData: payload,
                },
            };
        },
        setStratumsDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    stratumsData: payload,
                },
            };
        },
        setApproximationDataLoading(state, payload) {
            return {
                ...state,
                approximationData: {
                    ...state.approximationData,
                    loading: payload,
                },
            };
        },
        setFirstIndicatorDataLoading(state, payload) {
            return {
                ...state,
                firstIndicator: {
                    ...state.firstIndicator,
                    loading: payload,
                },
            };
        },
        setSecondIndicatorDataLoading(state, payload) {
            return {
                ...state,
                secondIndicator: {
                    ...state.secondIndicator,
                    loading: payload,
                },
            };
        },
        setStepsDataLoading(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    stepsData: payload,
                },
            };
        },
        /*** */
    },

    selectors: (slice, createSelector) => ({
        currentMethod() {
            return slice(state => {
                return state.methodsData ? state.methodsData.find(method => method.selected) : null;
            });
        },
        currentMethodSteps() {
            return slice(state => {
                const currentMethod = state.methodsData ? state.methodsData.find(method => method.selected) : null;
                return !isEmpty(currentMethod) ? state.stepsData[currentMethod?.key] : null;
            });
        },
        formattedSteps() {
            return slice(state => {
                return state.stepsData
                    ? Object.keys(state.stepsData).reduce((acc, methodKey) => {
                          return {
                              [methodKey]: state.stepsData[methodKey].rows,
                              ...acc,
                          };
                      }, {})
                    : null;
            });
        },
        datesRange() {
            return createSelector(
                rootState => rootState.desaturationCharacteristics.unsolvedResultParameters.stratumsData,
                stratumsData => {
                    const checkedStratums = (stratumsData ?? []).filter(stratum => stratum.checked);
                    return !isEmpty(checkedStratums)
                        ? checkedStratums.reduce(
                              (acc, cur) => {
                                  const curStart = cur?.period?.start;
                                  const accStart = acc.startDate;
                                  const curEnd = cur?.period?.end;
                                  const accEnd = acc.endDate;
                                  return {
                                      startDate:
                                          dayjs(curStart, 'MM.YYYY').valueOf() < dayjs(accStart, 'MM.YYYY').valueOf()
                                              ? curStart
                                              : accStart,
                                      endDate:
                                          dayjs(curEnd, 'MM.YYYY').valueOf() > dayjs(accEnd, 'MM.YYYY').valueOf()
                                              ? curEnd
                                              : accEnd,
                                  };
                              },
                              {
                                  startDate: dayjs().format('MM.YYYY'),
                                  endDate: dayjs().subtract(1000, 'month').format('MM.YYYY'),
                              }
                          )
                        : null;
                }
            );
        },
    }),

    effects: dispatch => ({
        async fetchStratums(params, state) {
            this.setStratumsDataLoading(true);
            try {
                const data = await api.desaturationCharacteristics.fetchStratums(params);
                this.setStratumsData(!isEmpty(data?.error) ? [] : data);
            } catch (error) {
                message.error('Не удалось получить данные по пластам');
                this.setStratumsData([]);
            }
            this.setStratumsDataLoading(false);
        },

        async calculate(payload, state) {
            const { unsolvedResultParameters, requestParameters } = payload;

            if (!isEmpty(unsolvedResultParameters)) {
                this.setSolvedResultParametersValue(unsolvedResultParameters);
                this.setMethodsDataLoading(true);
            }

            this.setGURDataLoading(true);
            this.setInjectBalanceDataLoading(true);
            this.setProductionPumpingDataLoading(true);
            this.setApproximationDataLoading(true);
            this.setFirstIndicatorDataLoading(true);
            this.setSecondIndicatorDataLoading(true);
            this.setStepsDataLoading(true);

            try {
                const calculationResult = await api.desaturationCharacteristics.fetchApproximation(requestParameters);
                const firstIndicatorValue = state.desaturationCharacteristics.firstIndicator.value;
                const secondIndicatorValue = state.desaturationCharacteristics.secondIndicator.value;
                const {
                    approximate,
                    base,
                    debitWaterCut,
                    gur,
                    injectBalance,
                    methods,
                    orfWaterCut,
                    owr,
                    productionPumping,
                    steps,
                    tempGur,
                    upTake,
                    upTakeWaterCut,
                    waterCut,
                    waterCutCumSum,
                } = calculationResult;

                const indicatorsList = {
                    base: base,
                    waterCut: waterCut,
                    waterCutCumSum: waterCutCumSum,
                    debitWaterCut: debitWaterCut,
                    orfWaterCut: orfWaterCut,
                    upTake: upTake,
                    owr: owr,
                    tempGur: tempGur,
                    upTakeWaterCut: upTakeWaterCut,
                };

                Object.keys(indicatorsList).forEach(indicator => {
                    if (!isEmpty(indicatorsList[indicator])) {
                        const { header, rows, axis, graphs } = indicatorsList[indicator];
                        this.setIndicators({
                            [indicator]: { tableData: { header, rows } || {}, graphData: { axis, graphs } || {} },
                        });
                        if (firstIndicatorValue === indicator) {
                            this.setFirstIndicatorData({
                                tableData: { header, rows } || {},
                                graphData: { axis, graphs } || {},
                            });
                        }

                        if (secondIndicatorValue === indicator) {
                            this.setSecondIndicatorData({
                                tableData: { header, rows } || {},
                                graphData: { axis, graphs } || {},
                            });
                        }
                    }
                });

                if (!isEmpty(approximate)) {
                    const { header, rows, axis, graphs } = approximate;
                    this.setApproximationData({ tableData: { header, rows } || {}, graphData: { axis, graphs } || {} });
                } else this.setApproximationData(null);

                if (!isEmpty(gur)) {
                    this.setGURData(gur);
                }

                if (!isEmpty(injectBalance)) {
                    this.setInjectBalanceData(injectBalance);
                }

                if (!isEmpty(productionPumping)) {
                    this.setProductionPumpingData(productionPumping);
                }

                // fetching methodsData only after the first calculation
                if (!isEmpty(unsolvedResultParameters)) {
                    if (!isEmpty(methods)) {
                        this.setMethodsData(methods.rows);
                    }
                }

                if (!isEmpty(steps)) {
                    this.setStepsData(steps);
                }
            } catch (error) {
                console.error(error);
                this.clearPreviousCalculation();
                message.error('Не удалось выполнить расчёт');
            }

            this.setGURDataLoading(false);
            this.setInjectBalanceDataLoading(false);
            this.setProductionPumpingDataLoading(false);
            this.setApproximationDataLoading(false);
            this.setFirstIndicatorDataLoading(false);
            this.setSecondIndicatorDataLoading(false);
            this.setStepsDataLoading(false);

            if (!isEmpty(unsolvedResultParameters)) {
                this.setMethodsDataLoading(false);
            }
        },

        clearPreviousCalculation() {
            this.setCurrentTab(CURRENT_TAB.calculationParameters);
            this.setGURData(null);
            this.setInjectBalanceData(null);
            this.setProductionPumpingData(null);
            this.setMethodsData(null);
            this.setStepsData(STEPS_INITIAL_STATE);
            this.setIndicators({
                base: null,
                debitWaterCut: null,
                orfWaterCut: null,
                owr: null,
                tempGur: null,
                upTake: null,
                upTakeWaterCut: null,
                waterCut: null,
                waterCutCumSum: null,
            });
            this.setApproximationData({
                tableData: null,
                graphData: null,
                loading: false,
            });
            this.setFirstIndicatorData({
                value: INDICATORS.base.id,
                label: INDICATORS.base.name,
                tableData: null,
                graphData: null,
                loading: false,
            });
            this.setSecondIndicatorData({
                value: INDICATORS.waterCut.id,
                label: INDICATORS.waterCut.name,
                tableData: null,
                graphData: null,
                loading: false,
            });
        },

        dropStoreToDefault() {
            this.setCurrentLeftWidget(LEFT_WIDGET.spreadsheets);
            this.setCurrentRightWidget(RIGHT_WIDGET.GUR);
            this.setSolved(false);
            this.setSolvedResultParametersValue(DEFAULT_PARAMETERS_FOR_SOLVING);
            this.setUnsolvedResultParametersValue(DEFAULT_PARAMETERS_FOR_SOLVING);
            this.setStratumsData([]);
            this.clearPreviousCalculation();
        },
    }),
};

export default desaturationCharacteristicsModel;
